import {
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Popper,
  AutocompleteRenderInputParams,
  AutocompleteRenderGroupParams,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { NotifyAPI } from '../api/NotifyAPI';
import { SessionStore } from '../services/SessionStore';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, AppDispatch, AppStore } from '../store';
import { UrlHelper } from '../services/UrlHelper';
import {
  getLanguageCode,
  getViewId,
  GetToken,
  isSubModelAssigned,
  isRestrictedState,
  isReadOnlyAccess,
  isTempConfiguration,
} from '../services';
import { isFeatureActive } from '../services/ClaimsHelper';
import { useTranslation } from 'react-i18next';
import { ViewHandler } from './ViewHandler';
import { LanguageSetting } from './LanguageSetting';
import { CurrencySetting } from './CurrencySetting';
import {
  EActionType,
  PopperModifier,
  ESessionStore,
  EUrlParams,
  ETabValue,
  EPageNames,
  ELocalStore,
} from '../data/Constants';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WebAssetSharpIcon from '@mui/icons-material/WebAssetSharp';
import WebAssetOffIcon from '@mui/icons-material/WebAssetOff';
import SearchIcon from '@mui/icons-material/Search';
import DisabledByDefaultRoundedIcon from '@mui/icons-material/DisabledByDefaultRounded';
import { AppAuthContext } from '../services/Contexts';
import { SaveAndClose } from './SaveAndClose';
import { MessagesPopover } from './MessagesPopover';
import { IsDefaultFlowInSessionStore } from '../services/SessionStoreHelperFunctions';
import { setUserSettings } from '../store/states/UserSettingsSlice';
import {
  setCurrentActiveTab,
  setIsConfigurationChanged,
  updateAutoSelectedDialog,
} from '../store/states/ApplicationSettingsSlice';
import { getProductCatalog } from '../store/states/ProductCatalogSlice';
import { getInternalProductCatalog } from 'store/states/InternalProductCatalogSlice';
import { setError } from '../store/states/ErrorSlice';
import {
  changeLandingTab,
  clearConfiguration,
  clearOptionalItem,
  loadConfiguration,
  setLoaderMessage,
  updateSearchKey,
  setSearchFlagToSectionsVariables,
} from '../store/states/ConfigurationSlice';
import ActionDialog from './ActionDialog';
import { getMyConfigurations } from '../store/states/UserSlice';
import AutoCompleteControl from '../components/InputComponents/AutoCompleteControl';
import { TFunction } from 'i18next';
import { AuthContextProps } from 'oidc-react';
import { LocalStore } from '../services/LocalStore';
import type {
  IApplicationSettings,
  IExtendedConfigureResponse,
  IProductSearchBarProps,
  ISearchDetails,
  ISearchOption,
  IUpdateSearchProps,
} from '../../types';
import eraserDisabledIcon from '../assets/svg/eraser_disabled.svg';
import eraserIcon from '../assets/svg/eraser.svg';

const displayCodeIcon = () => {
  const showCode = AppStore.getState().userSettings.showCode;
  if (showCode) {
    return <VisibilityIcon fontSize="medium" />;
  } else {
    return <VisibilityOffIcon fontSize="medium" />;
  }
};

const displayResetIcon = () => {
  const disableResetButton = disableResetIcon();

  if (disableResetButton) {
    return (
      <img className="rest-icon" src={eraserDisabledIcon} alt="reset-disable" />
    );
  } else {
    return <img className="rest-icon" src={eraserIcon} alt="reset-enable" />;
  }
};

const disableResetIcon = () => {
  const configuration = AppStore.getState().configuration;
  const applicationSettings = AppStore.getState().applicationSettings;
  const savedState = configuration.savedConfiguration?.state ?? '';

  return (
    isRestrictedState(savedState) ||
    !applicationSettings.isConfigurationChanged ||
    isTempConfiguration(savedState)
  );
};

function disableClearAllBtn() {
  const configuration = AppStore.getState().configuration;
  const savedState = configuration.savedConfiguration?.state ?? '';
  const accessState = configuration.access;

  return isRestrictedState(savedState) || isReadOnlyAccess(accessState);
}

// Display product list in AutoComplete when length of input text is greater than 2
const searchWithInConfiguration = (e: any, dispatch: AppDispatch, showCode: boolean) => {
  if (e.target.value && (e.target.value.length > 2 || e.target.value.length === 0)) {
    dispatch(
      setSearchFlagToSectionsVariables({
        searchKey: e.target.value,
        showCode: showCode,
        highlightResults: false,
      })
    );
  }
};

const updateProductCatalog = (newVal: string | ISearchOption, props: IUpdateSearchProps): void => {
  const {
    token,
    configuration,
    setSearchValue,
    location,
    productSettings,
    internalProductSettings,
    searchValue,
    user,
    dispatch,
    showCode,
  } = props;

  /* searchKey.title
  // This used when search is performed using descirption / name of Feature / Option like "SmartCT Angio Ent" --> "SmartCT Angio Ent (NNAT060)"
  // searchKey.displayName 
  // This used when search is performed using Feature / Option code like "NNAT060" --> "SmartCT Angio Ent (NNAT060)"
  */
  let searchKey = newVal ?? '';
  let displayName: string;

  if (typeof searchKey === 'object' && searchKey !== null) {
    displayName = searchKey.displayName ?? searchKey.title ?? '';
  } else {
    displayName = searchKey;
  }

  searchKey = (typeof searchKey === 'string' ? searchKey : searchKey.title).trim();

  setSearchValue(displayName.trim());

  if (
    location.pathname === EPageNames.LandingPage &&
    configuration.checkLandingTab.tabValue === ETabValue.ProductPortfolio &&
    (searchKey || searchValue)
  ) {
    dispatch(updateSearchKey({ searchKey: searchKey }));
    dispatch(
      getProductCatalog({
        token: token,
        page: 1,
        limit: productSettings.productCatalogDetails.limit,
        searchKey: searchKey,
        agCodes: productSettings.productCatalogDetails.agCodes,
        channelCodes: productSettings.productCatalogDetails.channelCodes,
      })
    );
  } else if (
    location.pathname === EPageNames.LandingPage &&
    configuration.checkLandingTab.tabValue === ETabValue.InternalProductPortfolio &&
    (searchKey || searchValue)
  ) {
    dispatch(updateSearchKey({ searchKey: searchKey }));
    dispatch(
      getInternalProductCatalog({
        token: token,
        page: 1,
        limit: internalProductSettings.productCatalogDetails.limit,
        searchKey: searchKey,
        agCodes: internalProductSettings.productCatalogDetails.agCodes,
        channelCodes: internalProductSettings.productCatalogDetails.channelCodes,
      })
    );
  } else if (
    location.pathname === EPageNames.LandingPage &&
    configuration.checkLandingTab.tabValue === ETabValue.MyConfiguration &&
    (searchKey || searchValue)
  ) {
    dispatch(updateSearchKey({ searchKey: searchKey }));
    const { channelCodes, applicationIds, states, limit } = user.myConfigPageDetails;
    dispatch(
      getMyConfigurations({
        token: token,
        page: 1,
        limit: limit,
        searchKey: searchKey,
        applicationIds: applicationIds,
        channelCodes: channelCodes,
        states: states,
      })
    );
  } else {
    dispatch(
      setSearchFlagToSectionsVariables({
        searchKey: searchKey,
        showCode: showCode,
        highlightResults: true,
      })
    );
  }
};

const CloseWithoutSaveDialog = ({ onCloseWithoutSavingDialog, t }: any) => {
  return IsDefaultFlowInSessionStore() ? (
    <></>
  ) : (
    <Tooltip title={t('tooltip.closeWithoutSaving')}>
      <IconButton
        className={'action-panel-buttons icon-font-size closeWithoutSave-icon'}
        onClick={onCloseWithoutSavingDialog}
        data-testid="btn-close-configuration"
      >
        <DisabledByDefaultRoundedIcon />
      </IconButton>
    </Tooltip>
  );
};

const handleError = (
  err: any,
  setErrorMessage: (message: string) => void,
  onCloseDialog: () => void,
  t: TFunction,
  dispatch: AppDispatch
) => {
  if (err.response) {
    dispatch(
      setError({
        code: err.response.status ? err.response.status : 500,
        message: err.response.data ? err.response.data.message : err.message,
        page: 'notify',
        show: true,
      })
    );
    setErrorMessage(
      err.response.data
        ? err.response.data.message
        : err.message + '. ' + t('errorMessages.tryAgain')
    );
    onCloseDialog();
  }
  dispatch(setLoaderMessage(null));
};

const CustomPopper = function (props: any) {
  return <Popper {...props} placement="bottom" modifiers={PopperModifier} />;
};

const onSearchValueChange = (
  value: string,
  reason: string,
  dispatch: AppDispatch,
  showCode: boolean
) => {
  if (value === '' && reason === 'reset') {
    dispatch(updateSearchKey({ searchKey: value }));
    dispatch(
      setSearchFlagToSectionsVariables({
        searchKey: value,
        showCode: showCode,
        highlightResults: false,
      })
    );
  }
};

//This function returns value to show in the search text box
//when matching criteria is found then option.displayName or option.title is returned
//when no option matching the search criteria is found then searchValue is returned as a fallback
function getSearchOption(option: string | ISearchOption, searchValue: string): string {
  if (typeof option === 'object' && option.displayName) {
    return option.displayName;
  }

  if (typeof option === 'object' && option.title) {
    return option.title;
  }

  return searchValue;
}

//Search Field
const searchBox = (
  params: AutocompleteRenderInputParams,
  dispatch: AppDispatch,
  t: TFunction,
  showCode: boolean
) => {
  return (
    <TextField
      {...params}
      className="search-inner-text"
      placeholder={t('labels.search')}
      onChange={(e) => searchWithInConfiguration(e, dispatch, showCode)}
      slotProps={{
        ...params.slotProps,
        input: {
          ...params.slotProps.input,
          startAdornment: (
            <InputAdornment position="end" className="search-icon">
              <SearchIcon />
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

const renderGroup = (params: AutocompleteRenderGroupParams) => {
  return (
    <div key={params.group}>
      <span className="display-options-header">
        {params.group ? (
          <>
            <span>&nbsp;</span> {params.group}
          </>
        ) : (
          ''
        )}
      </span>
      {params.children}
    </div>
  );
};

const ProductSearchBar = (ProductSearchBarProps: IProductSearchBarProps) => {
  const { searchValue, setSearchValue, searchDetails } = ProductSearchBarProps;
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { token } = GetToken();

  const user = useSelector((state: AppState) => state.user);
  const productSettings = useSelector((state: AppState) => state.productCatalog);
  const internalProductSettings = useSelector((state: AppState) => state.internalProductCatalog);
  const configuration = useSelector((state: AppState) => state.configuration);
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const handleChange = (value: string) => {
    updateProductCatalog(value, {
      token,
      configuration,
      setSearchValue,
      location,
      productSettings,
      internalProductSettings,
      searchValue,
      dispatch,
      user,
      showCode,
    });
  };

  if (location.pathname === EPageNames.LandingPage) {
    return (
      <AutoCompleteControl
        value={searchValue}
        options={[]}
        disabled={false}
        id="product-search-box"
        multiple={false}
        freeSolo={true}
        clearOnBlur={true}
        renderInput={(params) => searchBox(params, dispatch, t, showCode)}
        className={'search-bar'}
        PopperComponent={CustomPopper}
        handleChange={handleChange}
      />
    );
  } else {
    const options = searchDetails.matches.map((option: ISearchOption) => {
      const familyName = option.familyName.toUpperCase();
      return {
        ...option,
        familyName: familyName,
      };
    });

    const sortedOptions = options.toSorted(
      (a: ISearchOption, b: ISearchOption) => -b.familyName.localeCompare(a.familyName)
    );

    return (
      <>
        {searchValue.length > 0 ? (
          <span className="result-found">
            {t('labels.result')}: {searchDetails.count}
          </span>
        ) : null}
        <AutoCompleteControl
          value={searchValue}
          options={sortedOptions}
          disabled={false}
          id="product-search-box"
          multiple={false}
          freeSolo={true}
          clearOnBlur={!searchValue}
          renderInput={(params) => searchBox(params, dispatch, t, showCode)}
          className={'search-bar'}
          onInputChange={(_e, value, reason) => {
            onSearchValueChange(value.trim(), reason, dispatch, showCode);
          }}
          groupBy={(option: ISearchOption) => option.familyName}
          getOptionLabel={(option) => getSearchOption(option, searchValue)}
          renderGroup={(params) => renderGroup(params)}
          PopperComponent={CustomPopper}
          handleChange={handleChange}
        />
      </>
    );
  }
};

const closeTab = (
  dialogType: string,
  onCloseDialog: () => void,
  navigate: (path: string, replace: object) => void,
  configurationId: string | null,
  auth: AuthContextProps
) => {
  onCloseDialog();
  if (dialogType === EActionType.Notify) {
    navigate('/notified', { replace: true });
  } else {
    navigate('/closeTab', { replace: true });
    window.parent.postMessage(
      { message: 'configuration_close', cid: configurationId },
      document.referrer
    );
  }
  setTimeout(() => {
    auth.signOut();
    SessionStore.clear();
  }, 2000);
};

const tabChanged = (
  configuration: IExtendedConfigureResponse,
  setSearchValue: (value: React.SetStateAction<string>) => void,
  dispatch: AppDispatch
) => {
  if (configuration.checkLandingTab.isTabChanged) {
    setSearchValue('');
    dispatch(updateSearchKey({ searchKey: '' }));
    dispatch(changeLandingTab({ value: false, tabValue: configuration.checkLandingTab.tabValue }));
  }
};

const closeDialog = (
  reason: string,
  setDialog: (value: React.SetStateAction<string | TFunction>) => void,
  setShowDialog: (value: React.SetStateAction<boolean>) => void
) => {
  if (reason !== 'backdropClick') {
    setDialog('');
    setShowDialog(false);
  }
};

const clearAllConfiguration = async (props: any) => {
  const { res, setErrorMessage, onCloseDialog, t, handleSuccess, dispatch } = props;
  if (res.error) {
    handleError(res, setErrorMessage, onCloseDialog, t, dispatch);
  } else {
    dispatch(clearOptionalItem());
    handleSuccess(EActionType.ClearAll);
  }
};
const resetActiveTab = (
  configuration: IExtendedConfigureResponse,
  applicationSettings: IApplicationSettings,
  dispatch: AppDispatch
) => {
  //reset to previous active tab while reset cofiguration from submodel
  const modelPath = UrlHelper.getSearchParameter(EUrlParams.Model);
  const rootModel = configuration?.data?.product?.id ?? '';
  let activeTab =
    rootModel === undefined
      ? undefined
      : applicationSettings?.productsActiveTab[rootModel]?.tabIndex;

  if (modelPath) {
    activeTab = applicationSettings?.productsActiveTab[modelPath]?.tabIndex;
    const path = modelPath ? [rootModel, ...modelPath.split('.')] : [rootModel];
    if (isSubModelAssigned(configuration, path.slice(1))) {
      dispatch(setCurrentActiveTab({ activeTab: activeTab }));
    }
  } else {
    dispatch(setCurrentActiveTab({ activeTab: activeTab }));
  }
};

/**
 * Renders the action panel which includes functionality for
 * saving, canceling and set preferences
 * @param {IComponentProps} props the properties for the action panel component
 * @returns {JSX.Element} the action panel component
 */

export const ActionPanel = () => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [dialog, setDialog] = useState<string | TFunction>('');
  const [searchValue, setSearchValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [header, setHeader] = useState('');
  const [dialogType, setDialogType] = useState('');
  const [searchDetails, setSearchDetails] = useState<ISearchDetails>({ count: 0, matches: [] });
  const [showAutoSelectionDialog, setShowAutoSelectionDialog] = useState(true);
  const languageCode = getLanguageCode();
  const navigate = useNavigate();
  const configurationId = UrlHelper.getSearchParameter(EUrlParams.ConfigurationId);

  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const userPreferences = useSelector((state: AppState) => state.userPreferences);
  const configuration = useSelector((state: AppState) => state.configuration);
  const featureFlags = useSelector((state: AppState) => state.claimsData.featureFlags);
  const dispatch = useDispatch<AppDispatch>();

  const { token, auth } = GetToken();
  const loadProduct = async () => {
    await dispatch(
      loadConfiguration({
        configurationId: configurationId ?? '',
        languageCode: languageCode,
        token: token,
        viewId: SessionStore.get(ESessionStore.ViewId),
      })
    );
    dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
    resetActiveTab(configuration, applicationSettings, dispatch);
  };

  useEffect(() => {
    tabChanged(configuration, setSearchValue, dispatch);
  }, [configuration.checkLandingTab.isTabChanged]);

  useEffect(() => {
    setSearchDetails(configuration.search.details);
  }, [configuration.search.details.matches]);

  useEffect(() => {
    dispatch(setUserSettings({ showCode: userPreferences?.showCode }));
  }, [userPreferences?.showCode]);

  useEffect(() => {
    // Initialize showAutoSelectionDialog state from application settings only if feature flag is enabled
    const isFeatureEnabled = isFeatureActive(featureFlags.ASItemsDialog);
    if (!isFeatureEnabled) {
      return;
    }
    const storedAutoSelectState = applicationSettings.showAutoSelections;
    setShowAutoSelectionDialog(storedAutoSelectState);
    const showAutoSelections: boolean = isFeatureEnabled
      ? LocalStore.get(ELocalStore.ShowAutoSelections)
      : false;
    if (!showAutoSelections) {
      dispatch(updateAutoSelectedDialog({ showAutoSelections: showAutoSelections ?? true }));
    }
  }, [applicationSettings.showAutoSelections, featureFlags.ASItemsDialog]);

  const onResetDialog = () => {
    setDialogType(EActionType.Reset);
    setErrorMessage('');
    setHeader(t('resetDialog.resetChanges'));
    setDialog(t('resetDialog.resetConfirmationMessage'));
    setShowDialog(true);
  };
  const onAutoSelectionClick = () => {
    const newBlockAutoSelectState = !showAutoSelectionDialog;
    dispatch(updateAutoSelectedDialog({ showAutoSelections: newBlockAutoSelectState }));
    setShowAutoSelectionDialog(newBlockAutoSelectState);
  };

  const onClearAllDialog = () => {
    setDialogType(EActionType.ClearAll);
    setErrorMessage('');
    setHeader(t('clearAllDialog.clearAllChanges'));
    setDialog(t('clearAllDialog.clearAllConfirmationMessage'));
    setShowDialog(true);
  };

  const onCloseWithoutSavingDialog = () => {
    setDialogType(EActionType.CloseWithoutSaving);
    setErrorMessage('');
    setHeader(t('closeWithoutSavingDialog.closeWithoutSavingChanges'));
    setDialog(t('closeWithoutSavingDialog.closeWithoutSavingConfirmationMessage'));
    setShowDialog(true);
  };

  const onCloseDialog = (_event?: any, reason?: any) => {
    closeDialog(reason, setDialog, setShowDialog);
  };

  const handleSuccess = (type: string) => {
    switch (type) {
      case EActionType.Reset: {
        setShowDialog(false);
        loadProduct();
        break;
      }
      case EActionType.ClearAll: {
        setShowDialog(false);
        dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
        dispatch(setLoaderMessage(null));
        break;
      }
      default:
        break;
    }
  };

  const reset = () => {
    setErrorMessage('');
    dispatch(setLoaderMessage(EActionType.Reset));
    NotifyAPI.reset({ configurationId: SessionStore.get(ESessionStore.ConfigurationId), token })
      .then(() => {
        handleSuccess(EActionType.Reset);
      })
      .catch((err) => {
        handleError(err, setErrorMessage, onCloseDialog, t, dispatch);
      });
  };

  const clearAll = () => {
    const viewId: string = getViewId();
    onCloseDialog();
    setErrorMessage('');
    dispatch(setLoaderMessage(EActionType.ClearAll));
    dispatch(
      clearConfiguration({
        configurationId: SessionStore.get(ESessionStore.ConfigurationId),
        token: token,
        viewId: viewId,
      })
    )
      .then(async (res: any) => {
        clearAllConfiguration({
          res,
          setErrorMessage,
          onCloseDialog,
          t,
          configurationId,
          languageCode,
          token,
          applicationSettings,
          handleSuccess,
          dispatch,
        });
      })
      .catch((err: any) => {
        handleError(err, setErrorMessage, onCloseDialog, t, dispatch);
      });
  };

  // Method to call the Notify API and handle error if Error Occurs
  const action = () => {
    if (dialogType === EActionType.ClearAll) {
      clearAll();
    } else if (dialogType === EActionType.CloseWithoutSaving) {
      closeTab(dialogType, onCloseDialog, navigate, configurationId, auth);
    } else {
      reset();
    }
  };

  const onToggleCodes = () => {
    dispatch(setUserSettings({ showCode: !showCode }));
  };

  const disableResetButton = disableResetIcon();
  const disableClearAllButton = disableClearAllBtn();

  return (
    <AppAuthContext.Provider value={token}>
      <Box className="rowFlex">
        <ActionDialog
          showDialog={showDialog}
          onCloseDialog={onCloseDialog}
          header={header}
          errorMessage={errorMessage}
          dialog={dialog}
          action={action}
          dialogType={dialogType}
        />

        {token && (
          <ProductSearchBar
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchDetails={searchDetails}
          />
        )}

        <LanguageSetting />

        {token && configurationId && (
          <>
            <CurrencySetting />
            <MessagesPopover />
            <ViewHandler />
            <Tooltip title={t('userPreference.showCode')}>
              <span>
                <IconButton
                  onClick={onToggleCodes}
                  disabled={!configurationId}
                  className="code-icon action-panel-buttons icon-font-size"
                  data-testid="btn-show-code"
                >
                  {displayCodeIcon()}
                </IconButton>
              </span>
            </Tooltip>
            {isFeatureActive(featureFlags.ASItemsDialog) && (
              <Tooltip title={t('tooltip.showAssignment')}>
                <IconButton
                  onClick={onAutoSelectionClick}
                  className="code-icon action-panel-buttons icon-font-size"
                  data-testid="btn-toggle-conflict"
                >
                  {showAutoSelectionDialog ? <WebAssetSharpIcon /> : <WebAssetOffIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('tooltip.reset')}>
              <span>
                <Button
                  onClick={onResetDialog}
                  disabled={disableResetButton}
                  className="eraserIcon"
                  data-testid="btn-reset-configuration"
                >
                  {displayResetIcon()}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t('tooltip.clearAll')}>
              <span>
                <IconButton
                  onClick={onClearAllDialog}
                  disabled={disableClearAllButton}
                  className="clearAllButton action-panel-buttons icon-font-size"
                  data-testid="btn-clear-configuration"
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
            {IsDefaultFlowInSessionStore() && <SaveAndClose configuration={configuration} />}
            <CloseWithoutSaveDialog onCloseWithoutSavingDialog={onCloseWithoutSavingDialog} t={t} />
          </>
        )}
      </Box>
    </AppAuthContext.Provider>
  );
};
